/**
 * An ⓘ that explains a column: on hover or keyboard focus on a computer, on a tap on a phone. The
 * note is placed on the page (not inside the table), so a scrolling table never cuts it off.
 * `more` adds a "More in Help" link to the part's help page (the note then stays open while the
 * pointer is on it, so the link can be reached).
 */
import { useEffect, useId, useRef, useState } from "react";
import { helpUrl } from "../features/help/HelpButton";

const W = 264;

export function InfoTip({ label, text, more }: { label: string; text: string; more?: string }) {
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const leaving = useRef<number | null>(null);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  // A tap focuses the button (which opens the note) and then clicks it: that click must not close it.
  const openedAt = useRef(0);

  const show = () => {
    openedAt.current = Date.now();
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const half = W / 2 + 8;
    setAt({ x: Math.min(Math.max(r.left + r.width / 2, half), window.innerWidth - half), y: r.bottom + 6 });
  };
  const hide = () => setAt(null);
  // With a link in the note, moving from the ⓘ onto the note keeps it open.
  const hideSoon = () => {
    if (!more) return hide();
    leaving.current = window.setTimeout(hide, 250);
  };
  const stay = () => {
    if (leaving.current !== null) window.clearTimeout(leaving.current);
    leaving.current = null;
  };

  useEffect(() => {
    if (!at) return;
    const away = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !btn.current?.contains(e.target as Node) && !tip.current?.contains(e.target as Node)) hide();
    };
    // Opening can scroll the button into view: only a later scroll closes the note.
    const gone = () => {
      if (Date.now() - openedAt.current > 400) hide();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", away);
    window.addEventListener("scroll", gone, true);
    window.addEventListener("resize", gone);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", away);
      window.removeEventListener("scroll", gone, true);
      window.removeEventListener("resize", gone);
    };
  }, [at]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={`What "${label}" means`}
        aria-describedby={at ? id : undefined}
        // Hover with a mouse only: a tap also sends imitation mouse events that would close it.
        onPointerEnter={(e) => e.pointerType === "mouse" && show()}
        onPointerLeave={(e) => e.pointerType === "mouse" && hideSoon()}
        onFocus={show}
        onBlur={(e) => {
          if (!tip.current?.contains(e.relatedTarget as Node)) hide();
        }}
        onClick={(e) => {
          e.stopPropagation(); // not a sort
          if (at && Date.now() - openedAt.current > 400) hide();
          else show();
        }}
        // Drawn 16 px; on a phone its touch area reaches 44 px (::before).
        data-touch-44
        className="relative ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-[-2px] opacity-70 before:absolute before:-inset-[14px] before:content-[''] hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:before:hidden"
      >
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
          <path d="M8 7.2v4.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {at && (
        <span
          ref={tip}
          id={id}
          role="tooltip"
          onPointerEnter={stay}
          onPointerLeave={(e) => e.pointerType === "mouse" && hide()}
          className={`${more ? "" : "pointer-events-none "}fixed z-50 rounded-lg border px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal shadow-xl`}
          style={{ left: at.x, top: at.y, width: W, transform: "translateX(-50%)", background: "#1a1a36", borderColor: "#3a3a5c", color: "#e0e0e8", whiteSpace: "normal" }}
        >
          {text}
          {more && (
            <a
              href={helpUrl(more)}
              target="_blank"
              rel="noopener"
              onBlur={hide}
              className="mt-1.5 block font-semibold hover:underline"
              style={{ color: "#F48C34" }}
            >
              More in Help ›
            </a>
          )}
        </span>
      )}
    </>
  );
}
